import { useEffect, useRef, useState } from 'react';
import { RevvlyStatusIcon, type RevvlyStatusState } from '@admin-platform/ui/revvly-status-icon';
import releaseFiles from 'virtual:release-notes';
import './PlatformStatus.css';

/** Full URL of the API's public status, e.g. https://api.revvly.ai/health/status. */
const STATUS_URL = import.meta.env.VITE_STATUS_URL ?? '';

const POLL_MS = 60_000;
const POLL_TROUBLE_MS = 15_000;
const TIMEOUT_MS = 8_000;
/** One failed check is a blip (a deploy restarting a pod); two in a row is "can't reach". */
const FAILURES_BEFORE_UNREACHABLE = 2;

type State = 'checking' | 'ok' | 'degraded' | 'down' | 'unreachable';
interface Snapshot {
  state: State;
  slow: string[];
  /** The title of an incident Revvly posted, which says it best. */
  incident: string | null;
}

const sortKey = (v: string) => {
  const m = /^v(\d+)\.(\d+)\.(\d+)$/.exec(v);
  return m ? Number(m[1]) * 1e8 + Number(m[2]) * 1e4 + Number(m[3]) : -1;
};
/** This build's newest release: the status never names a release the site does not list. */
const LATEST = [...releaseFiles].sort((a, b) => sortKey(b.version) - sortKey(a.version))[0];

function usePlatformStatus(): Snapshot {
  const [snapshot, setSnapshot] = useState<Snapshot>({
    state: 'checking',
    slow: [],
    incident: null,
  });
  const failures = useRef(0);

  useEffect(() => {
    if (!STATUS_URL) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const check = async () => {
      let next: Snapshot | null = null;
      if (document.visibilityState !== 'hidden') {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
        try {
          const query = LATEST ? `?upToVersion=${encodeURIComponent(LATEST.version)}` : '';
          const res = await fetch(`${STATUS_URL}${query}`, {
            signal: controller.signal,
            credentials: 'omit',
          });
          if (!res.ok) throw new Error(String(res.status));
          const body = (await res.json()) as {
            state?: string;
            components?: { name: string; state: string }[];
            incident?: { title?: string } | null;
          };
          failures.current = 0;
          const state: State =
            body.state === 'degraded' || body.state === 'down' ? body.state : 'ok';
          const slow = (body.components ?? []).filter((c) => c.state !== 'ok').map((c) => c.name);
          next = { state, slow, incident: body.incident?.title ?? null };
        } catch {
          failures.current += 1;
          if (failures.current >= FAILURES_BEFORE_UNREACHABLE) {
            next = { state: 'unreachable', slow: [], incident: null };
          }
        } finally {
          clearTimeout(timeout);
        }
      }
      if (cancelled) return;
      if (next) setSnapshot(next);
      const trouble = failures.current > 0 || (next && next.state !== 'ok');
      timer = setTimeout(() => void check(), trouble ? POLL_TROUBLE_MS : POLL_MS);
    };

    void check();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return snapshot;
}

function describe({ state, slow, incident }: Snapshot): string {
  if (incident && (state === 'degraded' || state === 'down')) return incident;
  switch (state) {
    case 'checking':
      return 'Checking Revvly status';
    case 'degraded':
      return slow.length
        ? `${slow.join(' and ')} ${slow.length === 1 ? 'is' : 'are'} slow`
        : 'Part of Revvly is slow';
    case 'down':
      return 'Revvly is having a problem';
    case 'unreachable':
      return "Revvly can't be reached right now";
    default:
      return 'All systems normal';
  }
}

const faceFor = (state: State): RevvlyStatusState =>
  state === 'degraded' ? 'degraded' : state === 'down' || state === 'unreachable' ? 'down' : 'idle';

/**
 * Revvly's live status, drawn with the product glyph (never the AI's spark). Shown in the footer
 * on every page, and with the latest release on the Changelog. Hidden only when the build has no
 * status URL (local development), since then there is nothing true to say.
 */
export function PlatformStatus({ withLatest = false }: { withLatest?: boolean }) {
  const snapshot = usePlatformStatus();
  if (!STATUS_URL) return null;
  const face = faceFor(snapshot.state);
  const latest = withLatest && LATEST ? LATEST.version.replace(/\.0$/, '') : null;
  return (
    <p className={`platform-status platform-status--${face}`} role="status" aria-live="polite">
      <RevvlyStatusIcon state={face} className="platform-status__glyph" />
      <span>
        {latest && <span className="platform-status__latest">Revvly {latest} · </span>}
        {describe(snapshot)}
      </span>
    </p>
  );
}
